import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import ocrAdaptationService from '../../services/ocrAdaptationService';
import { OCRAdaptation, OCRAdaptationFormData } from '../../types/ocrAdaptation';

interface OCRAdaptationState {
  adaptations: OCRAdaptation[];
  defaultInstructionSet: string;
  loading: boolean;
  error: string | null;
}

const initialState: OCRAdaptationState = {
  adaptations: [],
  defaultInstructionSet: '',
  loading: false,
  error: null,
};

export const fetchOCRAdaptations = createAsyncThunk(
  'ocrAdaptations/fetchOCRAdaptations',
  async (params: { search?: string; active?: boolean } = {}, { rejectWithValue }) => {
    try {
      return await ocrAdaptationService.fetchAdaptations(params);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Nepodařilo se načíst OCR adaptace');
    }
  }
);

export const fetchDefaultInstructionSet = createAsyncThunk(
  'ocrAdaptations/fetchDefaultInstructionSet',
  async (_, { rejectWithValue }) => {
    try {
      return await ocrAdaptationService.fetchDefaultInstructionSet();
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Nepodařilo se načíst standardní instrukce');
    }
  }
);

export const createOCRAdaptation = createAsyncThunk(
  'ocrAdaptations/createOCRAdaptation',
  async (data: OCRAdaptationFormData, { rejectWithValue }) => {
    try {
      return await ocrAdaptationService.createAdaptation(data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.response?.data?.error || 'Nepodařilo se uložit OCR adaptaci');
    }
  }
);

export const updateOCRAdaptation = createAsyncThunk(
  'ocrAdaptations/updateOCRAdaptation',
  async ({ id, data }: { id: number; data: OCRAdaptationFormData }, { rejectWithValue }) => {
    try {
      return await ocrAdaptationService.updateAdaptation(id, data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.response?.data?.error || 'Nepodařilo se upravit OCR adaptaci');
    }
  }
);

export const toggleOCRAdaptation = createAsyncThunk(
  'ocrAdaptations/toggleOCRAdaptation',
  async (id: number, { rejectWithValue }) => {
    try {
      return await ocrAdaptationService.toggleAdaptation(id);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Nepodařilo se změnit stav OCR adaptace');
    }
  }
);

export const deleteOCRAdaptation = createAsyncThunk(
  'ocrAdaptations/deleteOCRAdaptation',
  async (id: number, { rejectWithValue }) => {
    try {
      return await ocrAdaptationService.deleteAdaptation(id);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Nepodařilo se smazat OCR adaptaci');
    }
  }
);

const ocrAdaptationSlice = createSlice({
  name: 'ocrAdaptations',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOCRAdaptations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOCRAdaptations.fulfilled, (state, action: PayloadAction<OCRAdaptation[]>) => {
        state.loading = false;
        state.adaptations = action.payload;
      })
      .addCase(fetchOCRAdaptations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchDefaultInstructionSet.fulfilled, (state, action: PayloadAction<string>) => {
        state.defaultInstructionSet = action.payload;
      })
      .addCase(createOCRAdaptation.fulfilled, (state, action: PayloadAction<OCRAdaptation>) => {
        state.adaptations.push(action.payload);
        state.adaptations.sort((a, b) => b.priority - a.priority || a.vendorName.localeCompare(b.vendorName, 'cs'));
      })
      .addCase(updateOCRAdaptation.fulfilled, (state, action: PayloadAction<OCRAdaptation>) => {
        const index = state.adaptations.findIndex(item => item.id === action.payload.id);
        if (index !== -1) state.adaptations[index] = action.payload;
        state.adaptations.sort((a, b) => b.priority - a.priority || a.vendorName.localeCompare(b.vendorName, 'cs'));
      })
      .addCase(toggleOCRAdaptation.fulfilled, (state, action: PayloadAction<OCRAdaptation>) => {
        const index = state.adaptations.findIndex(item => item.id === action.payload.id);
        if (index !== -1) state.adaptations[index] = action.payload;
      })
      .addCase(deleteOCRAdaptation.fulfilled, (state, action: PayloadAction<number>) => {
        state.adaptations = state.adaptations.filter(item => item.id !== action.payload);
      })
      .addMatcher(
        action => action.type.startsWith('ocrAdaptations/') && action.type.endsWith('/rejected'),
        (state, action) => {
          state.loading = false;
          state.error = action.payload as string;
        }
      );
  },
});

export const { clearError } = ocrAdaptationSlice.actions;
export default ocrAdaptationSlice.reducer;
